import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { storiesApi } from '../api/stories';
import { projectsApi } from '../api/projects';
import { isAbortError } from '../api/client';
import { loadChatSession, withOneQuietRetry } from '../utils/loadChatSession';
import { forgetOpenProject, isNotFound } from '../utils/lastProject';
import { PROJECT_OPEN_FAILED } from '../utils/writerCopy';

// storiesApi.get is wrapped inconsistently across the app; peel any data layers.
const unwrapStory = (res) => res?.data?.data || res?.data || res || null;

/**
 * Reconciles AppContext's currentProject/currentStory against whichever
 * route param the calling page has — the shared replacement for the
 * bespoke resolution effects ChatPage and StoryBoardPage used to carry
 * independently (F9 plan §3b). Called per-page, not centralized above the
 * router, since the two chains resolve differently:
 *
 *  - Board mode (`routeStoryId`): resolves a story record directly and
 *    hydrates context from it. Falls back to `currentStory` when there's
 *    no route id at all (bare `/board`).
 *  - Chat mode (`sessionId`): resolves session -> story -> project (or
 *    session -> project, for project-scoped sessions) and syncs context
 *    from that chain.
 *
 * Pass exactly one of `routeStoryId` / `sessionId` depending on which
 * page is calling this.
 */
export function useSyncStoryContext({ routeStoryId, sessionId, reloadKey = 0 } = {}) {
  const {
    currentStory,
    currentProject,
    projects,
    projectRestoreNonce,
    setProjectAndStory,
    setCurrentProject,
    setCurrentStory,
    setProjectOpenError,
  } = useApp();

  // A missing list (ChatPage's context mock, or first paint) is an empty
  // cache. Memoized so a missing list does not retrigger the board effect.
  const projectList = useMemo(
    () => (Array.isArray(projects) ? projects : []),
    [projects],
  );

  const noteProjectMiss = useCallback((err) => {
    if (isNotFound(err)) {
      forgetOpenProject();
      setProjectOpenError?.(null);
      return;
    }
    setProjectOpenError?.(PROJECT_OPEN_FAILED);
  }, [setProjectOpenError]);

  // ---- Board mode ----
  const boardMode = routeStoryId !== undefined;
  const storyId = boardMode ? (routeStoryId || currentStory?.id || null) : null;
  const [loadedStory, setLoadedStory] = useState(null);

  // Resolve the loaded story record. Prefer the in-context record;
  // otherwise fetch it (arriving via /board/:storyId cold).
  useEffect(() => {
    if (!boardMode) return;
    let cancelled = false;
    if (!storyId) { setLoadedStory(null); return; }
    if (currentStory?.id === storyId) {
      setLoadedStory(currentStory);
      return;
    }
    setLoadedStory(null);
    storiesApi.get(storyId)
      .then((res) => { if (!cancelled) setLoadedStory(unwrapStory(res)); })
      .catch(() => { if (!cancelled) setLoadedStory(null); });
    return () => { cancelled = true; };
  }, [boardMode, storyId, currentStory, projectRestoreNonce]);

  // Hydrate AppContext from the board URL so the surrounding chrome
  // (sidebar project, story-scoped top-bar tools) reflects the story
  // being viewed. Gated on the ROUTE id specifically (not the merged
  // storyId) — only a genuine cold URL visit should push context to
  // match the route; a bare /board falling back to context has nothing
  // new to hydrate from.
  useEffect(() => {
    if (!boardMode) return undefined;
    if (!routeStoryId || !loadedStory?.project_id) return undefined;
    const storyMatches = currentStory?.id === loadedStory.id;
    const projectMatches = currentProject?.id === loadedStory.project_id;
    if (storyMatches && projectMatches) return undefined;
    let cancelled = false;
    (async () => {
      let project = projectList.find((p) => p.id === loadedStory.project_id) || null;
      if (!project) {
        try {
          project = await projectsApi.get(loadedStory.project_id);
        } catch (err) {
          if (!cancelled) noteProjectMiss(err);
          return;
        }
      }
      if (cancelled || !project) return;
      setProjectOpenError?.(null);
      setProjectAndStory(project, loadedStory);
    })();
    return () => { cancelled = true; };
  }, [
    boardMode,
    routeStoryId,
    loadedStory,
    currentStory,
    currentProject,
    projectList,
    projectRestoreNonce,
    noteProjectMiss,
    setProjectAndStory,
    setProjectOpenError,
  ]);

  // ---- Chat mode ----
  const [session, setSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(!!sessionId);
  const [sessionError, setSessionError] = useState(null);

  useEffect(() => {
    if (sessionId === undefined) return undefined;
    if (!sessionId) { setSession(null); setSessionLoading(false); setSessionError(null); return undefined; }
    let cancelled = false;
    const controller = new AbortController();
    const signal = controller.signal;
    async function loadSession() {
      try {
        setSessionLoading(true);
        setSessionError(null);
        const sessionData = await loadChatSession(sessionId, { signal });
        if (cancelled) return;
        setSession(sessionData);

        if (sessionData.story_id) {
          const story = await withOneQuietRetry(
            () => storiesApi.get(sessionData.story_id, { signal }),
          );
          if (cancelled) return;
          const listed = projectList.find((p) => p.id === story.project_id);
          let project = listed;
          if (!project) {
            try {
              project = await withOneQuietRetry(
                () => projectsApi.get(story.project_id, { signal }),
              );
            } catch (err) {
              if (cancelled || isAbortError(err)) return;
              noteProjectMiss(err);
              throw err;
            }
          }
          if (!cancelled) {
            setProjectOpenError?.(null);
            setProjectAndStory(project, story);
          }
        } else if (sessionData.project_id) {
          let project;
          try {
            project = projectList.find((p) => p.id === sessionData.project_id)
              || await withOneQuietRetry(
                () => projectsApi.get(sessionData.project_id, { signal }),
              );
          } catch (err) {
            if (cancelled || isAbortError(err)) return;
            noteProjectMiss(err);
            throw err;
          }
          if (!cancelled) {
            setCurrentProject(project);
            // This session is project-scoped (no story_id) — e.g. series
            // project-level Discovery. Clear any story left over from a
            // previously-viewed book, even within the same project:
            // AppContext's own currentProject effect otherwise "helpfully"
            // preserves a same-project currentStory (right for switching
            // between story-scoped tools), which is wrong here since the
            // session we just loaded authoritatively has no story. Left
            // stale, it made currentStory?.id falsely truthy for any
            // series-Discovery session visited after a book in the same
            // series — the root cause of the Start Book 1 chat banner
            // never rendering (it's gated on !currentStory?.id).
            setCurrentStory(null);
          }
        }
      } catch (err) {
        if (cancelled || isAbortError(err)) return;
        console.error('Failed to load session context:', err);
        setSessionError(err.message);
      } finally {
        if (!cancelled) setSessionLoading(false);
      }
    }
    loadSession();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // `projects` is only a cache here. Depending on it would reload the
    // session when the project list arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, reloadKey, projectRestoreNonce, noteProjectMiss, setProjectAndStory, setCurrentProject, setCurrentStory, setProjectOpenError]);

  return { storyId, loadedStory, session, sessionLoading, sessionError };
}
